import React, {PureComponent} from "react"
import styled from "styled-components"
import moment from "moment"

import {injectIntl, InjectIntl, FormattedMessage} from "@irati/utils/lang"

import Card from "@irati/ui/src/Layout/Card"
import theme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import {H2, Body, Label} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import ValidationButton from "@irati/ui/src/FormItem/ValidationButton"
import {Hr} from "@irati/ui/src/Layout"
import {RenewalOption} from "@irati/types"
import salesService from "@irati/sales/services/sales"
import authentication from "@irati/auth"
import buService from "@irati/billing/services/bu"
import Loading from "@irati/ui/src/Loading"
import Validation from "@irati/utils/validation"
import adminTracking, {RenewalPlanType, RenewalPlanPage, RenewalPlanSubSource} from "@irati-tracking/admin/admin"
import GenericModal from "@irati/ui/src/GenericModal"

import {CurrencyType} from "@irati/payments/enums"
import ErrorText from "@irati/ui/src/ErrorText"
import RenewalCard from "../../RenewalCard"
import RenewalHeader, {AddOns} from "../../Renewal/Header"
import Detail from "../../Renewal/Detail"
import {getAddOns} from "../CurrentPlan"
import getPlan from "./getPlan"

interface Props extends InjectIntl {
  winbackOption?: RenewalOption
  onCallback: (subSource: RenewalPlanSubSource) => void
  onTimeout: () => void
  subSource: RenewalPlanSubSource
  onBack: () => void
}

interface State {
  showConfirmRenewalDetails: boolean
  firstName?: string
  lastName?: string
  email?: string
  telephone?: string
  renewalDate?: string
  selectedQuote?: RenewalOption
  products: AddOns[]
  quotes?: RenewalOption[]
  etag?: string
  submit?: Promise<any> | null
  load?: Promise<any> | null
  currencyCode?: CurrencyType
  showConfirmationModal: boolean
  error: boolean
}

const LeftWidth = styled.div`
  max-width: 445px;
`

class RenewalQuoteSelection extends PureComponent<Props, State> {
  state: State = {
    selectedQuote: undefined,
    quotes: [],
    showConfirmRenewalDetails: false,
    products: [],
    load: null,
    showConfirmationModal: false,
    error: false
  }

  componentDidMount() {
    const businessId = authentication.business().uuid
    const {subSource, winbackOption, formatMessage} = this.props

    const load = Promise.all([
      buService.getCurrentPlan({businessId: authentication.business().uuid!}),
      salesService.getRenewalQuote(businessId)
    ]).then(([{endDate}, {options, etag, products, seats, signatureContact, currency}]) => {
      const selectedQuote = options[1]
      adminTracking.renewalPlan({
        stage: 2,
        subSource,
        type: RenewalPlanType.renewalFlow,
        page: RenewalPlanPage.newplanStage,
        plan: getPlan(selectedQuote)
      })
      this.setState({
        renewalDate: moment(endDate).format("DD MMM YYYY"),
        etag,
        products: getAddOns(products, seats, formatMessage),
        quotes: options,
        selectedQuote,
        firstName: signatureContact.firstName,
        lastName: signatureContact.lastName,
        email: signatureContact.email,
        telephone: signatureContact.telephone,
        showConfirmRenewalDetails: !!winbackOption,
        currencyCode: currency
      })
    })
    this.setState({load})
  }

  buildEmailSchema = () => {
    return Validation.string("platform.defaultValidation.required").email("platform.fields.email.valid").max(255)
  }

  buildMobileValidation = () => {
    const {formatMessage} = this.props
    /* eslint-disable */
    return Validation.string("platform.defaultValidation.required")
      .transform((value) => (!value ? null : value))
      .min(7, formatMessage({id: "platform.fields.phone.validation.matches"}))
      .max(15, formatMessage({id: "platform.fields.phone.validation.max"}))
      .matches(
        /^[+]?([\d]{0,3})?[\(\.\-\s]?(([\d]{1,3})[\)\.\-\s]*)?(([\d]{3,5})[\.\-\s]?([\d]{4})|([\d]{2}[\.\-\s]?){4})$/,
        formatMessage({id: "platform.fields.phone.validation.matches"})
      )
    /* eslint-enable */
  }

  handleUpdateSelected = (selectedQuote: RenewalOption) => {
    adminTracking.renewalPlan({
      stage: 2,
      subSource: this.props.subSource,
      type: RenewalPlanType.renewalFlow,
      page: RenewalPlanPage.newplanStage,
      plan: getPlan(selectedQuote)
    })
    this.setState({selectedQuote})
  }

  onSubmit = () => {
    const fields = [this.state.firstName, this.state.lastName, this.state.email, this.state.telephone]
    const isValid = fields.every((field) => {
      return field && field !== ""
    })

    if (isValid) {
      this.setState({showConfirmationModal: true})
    } else {
      this.setState({error: true})
    }
  }

  handleRenewalSubmitted = () => {
    const {etag, selectedQuote, firstName, lastName, email, telephone} = this.state
    const {winbackOption, subSource, onCallback, onTimeout} = this.props
    const signatureContact = {
      firstName,
      lastName,
      email,
      telephone
    }
    const variables = {
      businessId: authentication.business().uuid,
      option: winbackOption || selectedQuote,
      etag,
      signatureContact
    }

    adminTracking.renewalPlan({
      stage: 3,
      page: RenewalPlanPage.confirmplanStage,
      subSource: RenewalPlanSubSource.confirmDetails,
      type: RenewalPlanType.renewalFlow,
      plan: getPlan(selectedQuote),
      contactDetail: signatureContact,
      action: "submitRenewal",
      acv: selectedQuote?.annualCost
    })
    const submit = salesService
      .acceptRenewalQuote(variables)
      .then(() => {
        this.setState({showConfirmationModal: false})
        onCallback(subSource)
      })
      .catch((err) => {
        const errorCode = err?.networkError?.errorData?.errorCode
        if (errorCode?.includes("SalesforceTimeout")) {
          this.setState({showConfirmationModal: false})
          onTimeout()
        } else {
          throw err
        }
      })
    this.setState({submit})
  }

  handleShowConfirmRenewalDetails = () => {
    adminTracking.renewalPlan({
      stage: 2,
      subSource: this.props.subSource,
      type: RenewalPlanType.renewalFlow,
      page: RenewalPlanPage.newplanStage,
      plan: getPlan(this.state.selectedQuote),
      action: "confirmDetails"
    })
    this.setState({showConfirmRenewalDetails: true})
  }

  handleBackToPlan = () => {
    this.setState({showConfirmRenewalDetails: false})
  }

  handleUpdateDetail = (editParamater: string, value: string) => {
    this.setState({error: false})
    const {selectedQuote} = this.state
    adminTracking.renewalPlan({
      stage: 3,
      page: RenewalPlanPage.confirmplanStage,
      subSource: RenewalPlanSubSource.confirmDetails,
      type: RenewalPlanType.renewalFlow,
      plan: getPlan(selectedQuote),
      editParamater
    })
    this.setState({[editParamater]: value})
  }

  render() {
    const {
      selectedQuote,
      showConfirmRenewalDetails,
      firstName,
      lastName,
      email,
      telephone,
      renewalDate,
      quotes,
      products,
      submit,
      load,
      currencyCode,
      showConfirmationModal,
      error
    } = this.state

    const {winbackOption, onBack} = this.props

    return (
      <>
        {!showConfirmRenewalDetails ? (
          <Loading load={load}>
            <Card bgColor={theme.colors.primary2} marginBottom={2} padding={5}>
              <Flex justifyContent="space-between">
                <LeftWidth>
                  <RenewalHeader
                    title="admin.billing.renewals.newPlan.title"
                    body="admin.billing.renewals.newPlan.body"
                    addOns={products}
                  />
                </LeftWidth>
                <div>
                  <Flex gap={theme.spacing.one} marginBottom={1} justifyContent="flex-end">
                    {quotes?.map((quote, i) => (
                      <RenewalCard
                        key={i}
                        quote={quote}
                        isSelected={quote.termLength === selectedQuote?.termLength}
                        onUpdate={this.handleUpdateSelected}
                        isRecommended={i === 1}
                        oneYearQuote={i === 0 ? undefined : quotes?.find((q) => q.termLength === 12)}
                        currencyCode={currencyCode}
                      />
                    ))}
                  </Flex>
                  <Label color={theme.colors.greys.mediumLight}>
                    <FormattedMessage id="admin.billing.renewals.newPlan.lockIn" />
                  </Label>
                </div>
              </Flex>
            </Card>
            <Flex justifyContent="space-between">
              <Button tertiary onClick={onBack}>
                <FormattedMessage id="admin.billing.renewals.newPlan.backToCurrentPlan" />
              </Button>
              <Button primary onClick={this.handleShowConfirmRenewalDetails}>
                <FormattedMessage id="admin.billing.renewals.newPlan.confirm" />
              </Button>
            </Flex>
          </Loading>
        ) : (
          <>
            <Card bgColor={theme.colors.primary2} marginBottom={4} padding={5}>
              <Flex justifyContent="space-between">
                <div>
                  <RenewalHeader
                    title="admin.billing.renewals.newPlan.title"
                    body="admin.billing.renewals.confirmDetails.body"
                    addOns={products}
                  />
                </div>
                <RenewalCard
                  hideSelector
                  isSelected
                  quote={winbackOption || selectedQuote}
                  oneYearQuote={quotes[0]}
                  currencyCode={currencyCode}
                />
              </Flex>
            </Card>
            <H2 marginBottom={1}>
              <FormattedMessage id="admin.billing.renewals.planDetails.title" />
            </H2>
            <Body color={theme.colors.greys.medium} marginBottom={3}>
              <FormattedMessage id="admin.billing.renewals.planDetails.body" />
            </Body>
            <Detail
              currentValue={firstName}
              title="admin.billing.renewals.planDetails.firstName.title"
              editText="admin.billing.renewals.planDetails.firstName.edit"
              validation={Validation.string("platform.defaultValidation.required").test(
                "notJustSpaces",
                "platform.defaultValidation.notJustSpaces",
                (value: string) => value && value.trim().length !== 0
              )}
              onUpdate={(value) => this.handleUpdateDetail("firstName", value)}
            />
            <Detail
              currentValue={lastName}
              title="admin.billing.renewals.planDetails.lastName.title"
              editText="admin.billing.renewals.planDetails.lastName.title"
              validation={Validation.string("platform.defaultValidation.required").test(
                "notJustSpaces",
                "platform.defaultValidation.notJustSpaces",
                (value: string) => value && value.trim().length !== 0
              )}
              onUpdate={(value) => this.handleUpdateDetail("lastName", value)}
            />
            <Detail
              currentValue={email}
              title="admin.billing.renewals.planDetails.email.title"
              editText="admin.billing.renewals.planDetails.email.edit"
              validation={this.buildEmailSchema()}
              onUpdate={(value) => this.handleUpdateDetail("email", value)}
            />
            <Detail
              currentValue={telephone}
              title="admin.billing.renewals.planDetails.phone.title"
              editText="admin.billing.renewals.planDetails.phone.edit"
              validation={this.buildMobileValidation()}
              onUpdate={(value) => this.handleUpdateDetail("telephone", value)}
            />
            <Detail isEditable={false} title="admin.billing.renewals.planDetails.renewal.title" currentValue={renewalDate} />
            <Hr />
            <Label>
              <FormattedMessage id="admin.billing.renewals.newPlan.mandatoryFields" />
            </Label>
            <Flex justifyContent="space-between" marginBottom={2} marginTop={2}>
              <Button tertiary onClick={this.handleBackToPlan}>
                <FormattedMessage id="admin.billing.renewals.newPlan.back" />
              </Button>
              <ValidationButton primary onClick={this.onSubmit}>
                <FormattedMessage id="admin.billing.renewals.newPlan.submit" />
              </ValidationButton>
            </Flex>
            {error && (
              <Flex justifyContent="flex-end">
                <ErrorText>
                  <FormattedMessage id="admin.billing.renewals.newPlan.fieldsError" />
                </ErrorText>
              </Flex>
            )}
          </>
        )}
        <GenericModal hideActions show={showConfirmationModal} onHide={() => this.setState({showConfirmationModal: false})}>
          <H2 marginBottom={1}>
            <FormattedMessage id="admin.billing.renewals.newPlan.modal.header" />
          </H2>
          <Body marginBottom={3}>
            <FormattedMessage
              id="admin.billing.renewals.newPlan.modal.body"
              values={{
                firstName: firstName,
                lastName: lastName,
                email: email
              }}
            />
          </Body>
          <Button primary onClick={this.handleRenewalSubmitted} load={submit}>
            <FormattedMessage id="admin.billing.renewals.newPlan.modal.confirm" />
          </Button>
          <Loading load={submit} useLoader={false} />
        </GenericModal>
      </>
    )
  }
}

export default injectIntl(RenewalQuoteSelection)
